import { useId, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, Chip, Tab, Tabs, Typography } from "@mui/material";
import { AccountTreeOutlined } from "@mui/icons-material";
import { flowStages, flowActors as actors } from "./flowData";

function FlowDiagram({ stage }) {
  const id = useId().replace(/:/g, "");
  return (
    <Box sx={{ overflowX: "auto", border: "1px solid #dbe4ec", borderRadius: 2, bgcolor: "#fff" }}>
      <svg
        viewBox={`0 0 900 ${stage.height}`}
        role="img"
        aria-labelledby={`${id}-title ${id}-desc`}
        style={{ display: "block", width: "100%", minWidth: 720, fontFamily: "Arial, sans-serif" }}
      >
        <title id={`${id}-title`}>{stage.title}</title>
        <desc id={`${id}-desc`}>{stage.description}</desc>
        <defs>
          <marker
            id={`${id}-arrow`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
          </marker>
        </defs>
        {stage.edges.map((edge, index) => (
          <g key={index}>
            <polyline
              points={edge.points.map((point) => point.join(",")).join(" ")}
              fill="none"
              stroke="#64748b"
              strokeWidth="1.8"
              strokeLinejoin="round"
              markerEnd={`url(#${id}-arrow)`}
            />
            {edge.label && (
              <text
                x={edge.labelAt[0]}
                y={edge.labelAt[1]}
                textAnchor="middle"
                fill="#334155"
                fontSize="12"
                fontWeight="700"
              >
                {edge.label}
              </text>
            )}
          </g>
        ))}
        {stage.nodes.map((node) => {
          const style = actors[node.actor] || { color: "#195b78", fill: "#edf5fa" };
          const state = node.kind === "state" || node.kind === "end";
          const exception = node.kind === "exception";
          const color = exception ? "#b91c1c" : style.color;
          const fill = exception ? "#fef2f2" : style.fill;
          const startY =
            node.y + node.h / 2 - ((node.lines.length - 1) * 17) / 2 + (node.actor ? 10 : 4);
          return (
            <g key={node.id}>
              {node.kind === "decision" ? (
                <polygon
                  points={`${node.x + node.w / 2},${node.y} ${node.x + node.w},${
                    node.y + node.h / 2
                  } ${node.x + node.w / 2},${node.y + node.h} ${node.x},${node.y + node.h / 2}`}
                  fill="#fffbeb"
                  stroke="#b45309"
                  strokeWidth="1.5"
                />
              ) : (
                <rect
                  x={node.x}
                  y={node.y}
                  width={node.w}
                  height={node.h}
                  rx={state ? 22 : 10}
                  fill={state ? "#195b78" : fill}
                  stroke={color}
                  strokeWidth="1.4"
                />
              )}
              {node.actor && (
                <text
                  x={node.x + node.w / 2}
                  y={node.y + 18}
                  textAnchor="middle"
                  fill={color}
                  fontSize="10"
                  fontWeight="700"
                >
                  {node.actor.toUpperCase()}
                </text>
              )}
              <text
                x={node.x + node.w / 2}
                y={startY}
                textAnchor="middle"
                fill={state ? "#fff" : "#172b41"}
                fontSize="12"
                fontWeight={state ? "700" : "500"}
              >
                {node.lines.map((line, index) => (
                  <tspan key={line} x={node.x + node.w / 2} dy={index ? 17 : 0}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
}
FlowDiagram.propTypes = { stage: PropTypes.object.isRequired };

export default function ConciliationFlowGuide() {
  const [tab, setTab] = useState(0);
  const stage = flowStages[tab];
  const id = useId();
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <AccountTreeOutlined sx={{ color: "#195b78" }} />
        <Typography component="h3" sx={{ fontSize: 23, fontWeight: 700, color: "text.primary" }}>
          Flujo de conciliación y pago
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 14, mb: 2 }}>
        Desde la generación automática de la llave hasta la revisión posterior al pago.
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        {["Reporte de la concesión", "Validación y pago", "Revisión posterior"].map(
          (state, index) => (
            <Box key={state} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {index > 0 && <span aria-hidden="true">→</span>}
              <Chip
                label={state}
                size="small"
                sx={{ bgcolor: "#edf5fa", color: "#195b78", fontWeight: 700 }}
              />
            </Box>
          )
        )}
      </Box>
      <Alert severity="info" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: 13 } }}>
        Los valores reportados por la concesión se guardan sobre la llave existente. No crean una
        llave de conciliación.
      </Alert>
      <Typography sx={{ fontSize: 13, mb: 2, color: "text.primary" }}>
        Peaje: estado de cuadre · Concesión: estado de validación y pago · Lote: pagado cuando todas
        sus concesiones están pagadas.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Etapas del flujo de conciliación"
        sx={{ mb: 2, bgcolor: "action.hover", "& .MuiTab-root": { fontSize: 12, minHeight: 48 } }}
      >
        {flowStages.map((item, index) => (
          <Tab
            key={item.id}
            id={`${id}-tab-${index}`}
            aria-controls={`${id}-panel-${index}`}
            label={`${index + 1}. ${item.title}`}
          />
        ))}
      </Tabs>
      <Box role="tabpanel" id={`${id}-panel-${tab}`} aria-labelledby={`${id}-tab-${tab}`}>
        <Typography sx={{ fontSize: 13, mb: 1.5, lineHeight: 1.7 }}>{stage.description}</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          {Object.entries(actors).map(([actor, style]) => (
            <Chip
              key={actor}
              size="small"
              label={actor}
              sx={{ bgcolor: style.fill, color: style.color, fontSize: 11 }}
            />
          ))}
        </Box>
        <FlowDiagram stage={stage} />
        <Typography sx={{ mt: 1.5, fontSize: 12, color: "text.secondary" }}>
          {stage.note}
        </Typography>
      </Box>
    </>
  );
}
